import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { EASE } from "./motion";

export const LINKS = [
  { label: "Home", hash: "#top" },
  { label: "The Experience", hash: "#experience" },
  { label: "Your Host", hash: "#host" },
  { label: "Professional Connections", hash: "#professional" },
  { label: "Sponsors", hash: "#sponsors" },
];

export function scrollToHash(hash) {
  const el = document.querySelector(hash);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: hash === "#top" ? 0 : -72, duration: 1.3 });
  } else {
    el.scrollIntoView({ behavior: "smooth" });
  }
}

export function Wordmark({ tone = "dark", className = "" }) {
  const color = tone === "dark" ? "text-cream" : "text-navy";
  return (
    <a
      href="#top"
      data-testid="wordmark"
      onClick={(e) => {
        e.preventDefault();
        if (window.__lenis) scrollToHash("#top");
      }}
      className={"inline-flex items-center gap-2 font-serif text-[1.35rem] font-semibold leading-none tracking-wide sm:text-[1.65rem] " + color + " " + className}
    >
      <svg width="20" height="20" viewBox="0 0 64 64" aria-hidden="true" className="shrink-0">
        <circle cx="32" cy="16" r="3.5" fill="#DFC9A2" />
        <path d="M8 34c7-6 11-6 17 0s10 6 16 0 11-6 15-2" stroke="#C5A880" strokeWidth="3.5" fill="none" strokeLinecap="round" />
        <path d="M8 48c7-6 11-6 17 0s10 6 16 0 11-6 15-2" stroke="#B89047" strokeWidth="3.5" fill="none" strokeLinecap="round" opacity="0.6" />
      </svg>
      <span>
        Chaplain <span className="italic opacity-80">at</span> Sea
      </span>
    </a>
  );
}

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (hash) => (e) => {
    e.preventDefault();
    setOpen(false);
    window.__lenis ? window.__lenis.start() : null;
    setTimeout(() => scrollToHash(hash), open ? 250 : 0);
  };

  const tone = scrolled && !open ? "light" : "dark";

  return (
    <>
      <header
        data-testid="site-nav"
        className={
          "fixed inset-x-0 top-0 z-40 transition-all duration-500 " +
          (scrolled
            ? "bg-cream/90 backdrop-blur-md border-b border-navy/10 py-3"
            : "bg-transparent py-5")
        }
      >
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-5 sm:px-8">
        <div className="flex flex-col">
          <Wordmark tone={tone} />
          <span
            data-testid="hero-brand-subtitle"
            className={
              "overflow-hidden font-mono text-[10px] uppercase leading-relaxed tracking-[0.16em] text-gold-light transition-all duration-500 sm:text-[11px] " +
              ((scrolled && !open) ? "mt-0 max-h-0 opacity-0" : "mt-1.5 max-h-12 max-w-[240px] opacity-100")
            }
          >
            Cruise Retreats for Chaplains and Those Exploring Chaplaincy
          </span>
        </div>
        <nav className="hidden items-center gap-5 lg:flex" aria-label="Primary">
            {LINKS.map((l) => (
              <a
                key={l.hash}
                href={l.hash}
                data-testid={"nav-link-" + l.label.toLowerCase().replace(/[^a-z]+/g, "-")}
                onClick={go(l.hash)}
                className={
                  "font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 hover:text-gold " +
                  (scrolled ? "text-navy/70" : "text-cream/80")
                }
              >
                {l.label}
              </a>
            ))}
            <a
              href="#interest"
              data-testid="nav-cta-interest"
              onClick={go("#interest")}
              className="rounded-sm border border-gold bg-gold/95 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-navy transition-all duration-300 hover:bg-gold-light"
            >
              Join the Interest List
            </a>
          </nav>
          <button
            data-testid="nav-mobile-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => {
              setOpen((o) => {
                if (window.__lenis) o ? window.__lenis.start() : window.__lenis.stop();
                return !o;
              });
            }}
            className={"lg:hidden " + (tone === "dark" ? "text-navy" : "text-cream")}
          >
            {open ? <X size={26} strokeWidth={1.5} /> : <Menu size={26} strokeWidth={1.5} />}
          </button>
        </div>
      </header>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-30 flex flex-col justify-center bg-navy px-8 lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <nav aria-label="Mobile" className="flex flex-col gap-6">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.hash}
                  href={l.hash}
                  onClick={go(l.hash)}
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5, ease: EASE }}
                  className="font-serif text-3xl text-cream"
                >
                  {l.label}
                </motion.a>
              ))}
              <motion.a
                href="#interest"
                data-testid="nav-mobile-cta"
                onClick={go("#interest")}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.5, ease: EASE }}
                className="mt-4 inline-flex items-center bg-gold px-5 py-3 text-center font-mono text-[12px] uppercase tracking-[0.16em] text-navy"
              >
                Join the Interest List
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
